import { useState, useEffect, useCallback } from "react";
import {
  authService,
  type User,
  type LoginCredentials,
} from "@shared/services/authService";

interface UseAuthReturn {
  // Estado de autenticación
  isAuthenticated: boolean;
  isAdmin: boolean;
  user: User | null;
  isLoading: boolean;

  // Información del token
  token: string | null;
  tokenInfo: {
    hasToken: boolean;
    isExpired: boolean;
    expiration?: Date;
    payload?: unknown;
  };

  // Funciones de autenticación
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;

  // Funciones de validación
  validateToken: () => { isValid: boolean; payload?: unknown; error?: string };
  isTokenExpiringSoon: () => boolean;

  // Funciones de utilidad
  checkAuth: () => void;
}

export function useAuth(): UseAuthReturn {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Verificar autenticación al cargar
  const checkAuth = useCallback(() => {
    try {
      const authenticated = authService.isAuthenticated();
      const admin = authService.isAdmin();
      const currentUser = authService.getUser();

      setIsAuthenticated(authenticated);
      setIsAdmin(admin);
      setUser(currentUser);
    } catch {
      setIsAuthenticated(false);
      setIsAdmin(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Login
  const login = useCallback(async (credentials: LoginCredentials) => {
    setIsLoading(true);
    try {
      const response = await authService.login(credentials);

      // El AuthService ya guarda los tokens y usuario en localStorage
      // Solo actualizamos el estado local
      setIsAuthenticated(true);
      setIsAdmin(authService.isAdmin());
      setUser(response.admin);
    } catch (error) {
      setIsAuthenticated(false);
      setIsAdmin(false);
      setUser(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await authService.logout();
      // El AuthService ya limpia localStorage
    } catch {
      // Error manejado silenciosamente
    } finally {
      // Siempre limpiar el estado local
      setIsAuthenticated(false);
      setIsAdmin(false);
      setUser(null);
      setIsLoading(false);
    }
  }, []);

  // Refresh token
  const refreshToken = useCallback(async () => {
    try {
      await authService.refreshToken();
      // Verificar autenticación después del refresh
      checkAuth();
    } catch (error) {
      // Si falla el refresh, hacer logout
      await logout();
      throw error;
    }
  }, [checkAuth, logout]);

  // Validar token
  const validateToken = useCallback(() => {
    return authService.validateToken();
  }, []);

  // Verificar si el token expira pronto
  const isTokenExpiringSoon = useCallback(() => {
    return authService.isTokenExpiringSoon();
  }, []);

  // Obtener información del token
  const tokenInfo = authService.getTokenInfo();
  const token = authService.getToken();

  // Verificar autenticación al montar el componente
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Escuchar cambios de autenticación para sincronizar estado (login/logout/refresh)
  useEffect(() => {
    const unsubscribe = authService.addAuthListener(() => {
      checkAuth();
    });
    return unsubscribe;
  }, [checkAuth]);

  // Verificar si el token expira pronto y refrescar automáticamente (solo si hay expiración válida)
  useEffect(() => {
    if (!isAuthenticated) return;

    const expiration = authService.getTokenExpiration();
    if (!expiration) return; // Si no hay expiración válida, no auto-refrescar

    const msRemaining = expiration.getTime() - Date.now();
    if (msRemaining <= 5 * 60 * 1000) {
      refreshToken().catch(() => {
        // Error manejado silenciosamente
      });
    }
  }, [isAuthenticated, refreshToken]);

  return {
    // Estado
    isAuthenticated,
    isAdmin,
    user,
    isLoading,

    // Token
    token,
    tokenInfo,

    // Funciones
    login,
    logout,
    refreshToken,
    validateToken,
    isTokenExpiringSoon,
    checkAuth,
  };
}
